import {
  IDObject,
  HeaderObject,
  TagsObject,
  FoldersObject,
  ID, PreviewObject,
} from 'types';

/**
 * Type of the program.
 * @type {string}
 */
export enum ProgramType {
  Recipe = 'recipe',
  Training = 'training',
  Lesson = 'Lesson',
  Webinar = 'Webinar',
}

/**
 * The interface of an program item.
 * @param {ID} id
 * @param {ProgramType} type
 */
export type ProgramItem<T extends object> =
  & T
  & {

  /**
   * Id of the instance.
   * @type {ID}
   */
  id: ID;

  /**
   * Type of the instance.
   * @type {ProgramType}
   */
  type: ProgramType;

}

/**
 * The interface of a day in the program.
 * @param {boolean} isWeekend
 * @param {boolean | undefined} video
 * @param {ProgramItem[]} items
 */
export interface ProgramDay<T extends object> {

  /**
   * Is the weekend.
   * @type {boolean}
   */
  isWeekend: boolean;

  /**
   * Video preview.
   * @type {boolean | undefined}
   */
  video?: PreviewObject;

  /**
   * Instance items in the day.
   * @type {ProgramItem[]}
   */
  items: ProgramItem<T>[];

}

/**
 * The type of an program object.
 * @implements {IDObject}
 * @implements {HeaderObject}
 * @implements {TagsObject}
 * @implements {FoldersObject}
 * @param {string | undefined} shortDescription
 * @param {ProgramDay[]} days
 */
export type ProgramObject<T extends object> =
  & IDObject
  & HeaderObject
  & TagsObject
  & FoldersObject
  & {

    /**
     * Short description of the program.
     * @type {string | undefined}
     */
    shortDescription?: string;

    /**
     * Days in the program.
     * @type {ProgramDay[]}
     */
    days: ProgramDay<T>[];

  }
;

/**
 * The interface of training approaches.
 * @type {number} count
 * @type {string} unit
 */
export interface TrainingApproaches {

  /**
   * Count.
   * @type {count}
   */
  count: number;

  /**
   * Amount.
   * @type {number}
   */
  amount: number;

  /**
   * Select unit.
   * @type {string}
   */
  unit:
    | 'seconds'
    | 'count'
    | 'minutes'
  ;

}

/**
 * The interface of training rest.
 * @type {number} amount
 * @type {string} unit
 */
export interface TrainingRest {

  /**
   * Amount.
   * @type {number}
   */
  amount: number;

  /**
   * Select unit.
   * @type {string}
   */
  unit:
    | 'seconds'
    | 'count'
    | 'minutes'
  ;

}

/**
 * The interface of an training program.
 * @param {string} itemType
 * @param {TrainingApproaches} approaches
 * @param {TrainingRest} rest
 */
export interface TrainingItem {

  /**
   * Type of item.
   * @type {string}
   */
  itemType:
    | 'warm-up'
    | 'main'
  ;

  /**
   * Approaches.
   * @type {TrainingApproaches}
   */
  approaches: TrainingApproaches;

  /**
   * Rest.
   * @type {TrainingRest}
   */
  rest: TrainingRest;

}

/**
 * The interface of an eat item.
 * @param {string} itemType
 */
export interface FoodItem {

  /**
   * Type of item.
   * @type {string}
   */
  itemType:
    | 'breakfast'
    | 'lunch'
    | 'dinner'
    | 'snack'
  ;

}
